import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Package,
  ShoppingCart,
  AlertTriangle,
  DollarSign,
  Calendar,
  BarChart3,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { getOrders } from '../../firebase/orderService';
import { getProducts } from '../../firebase/services';
import { format, startOfMonth, endOfMonth, isWithinInterval, subMonths } from 'date-fns';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ordersData, productsData] = await Promise.all([
        getOrders(),
        getProducts()
      ]);
      setOrders(ordersData);
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (preset) => {
    const now = new Date();
    let start, end;

    switch (preset) {
      case 'thisMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'lastMonth':
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      case 'last3Months':
        start = startOfMonth(subMonths(now, 2));
        end = endOfMonth(now);
        break;
      case 'last6Months':
        start = startOfMonth(subMonths(now, 5));
        end = endOfMonth(now);
        break;
      case 'allTime':
        start = new Date(0);
        end = now;
        break;
      default:
        start = startOfMonth(now);
        end = endOfMonth(now);
    }

    setDateRange({ start, end });
  };

  // Filter orders by date range
  const filteredOrders = orders.filter(order => {
    const orderDate = new Date(order.createdAt);
    return isWithinInterval(orderDate, { start: dateRange.start, end: dateRange.end });
  });

  // Calculate statistics
  const stats = {
    totalRevenue: filteredOrders.reduce((sum, order) => {
      if (order.status !== 'cancelled') {
        return sum + (order.totalAmount || 0);
      }
      return sum;
    }, 0),
    totalOrders: filteredOrders.length,
    deliveredOrders: filteredOrders.filter(o => o.status === 'delivered').length,
    pendingOrders: filteredOrders.filter(o => o.status === 'pending').length,
    cancelledOrders: filteredOrders.filter(o => o.status === 'cancelled').length,
    totalProducts: products.length,
    lowStockProducts: products.filter(p => (p.stock || 0) < 5 && (p.stock || 0) > 0).length,
    outOfStock: products.filter(p => (p.stock || 0) === 0).length,
    totalInventoryValue: products.reduce((sum, p) => sum + ((p.price || 0) * (p.stock || 0)), 0)
  };

  // Calculate previous period stats for comparison
  const previousPeriodEnd = new Date(dateRange.start);
  previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
  const periodLength = Math.ceil((dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24));
  const previousPeriodStart = new Date(previousPeriodEnd);
  previousPeriodStart.setDate(previousPeriodStart.getDate() - periodLength);

  const previousOrders = orders.filter(order => {
    const orderDate = new Date(order.createdAt);
    return isWithinInterval(orderDate, { start: previousPeriodStart, end: previousPeriodEnd });
  });

  const previousRevenue = previousOrders.reduce((sum, order) => {
    if (order.status !== 'cancelled') {
      return sum + (order.totalAmount || 0);
    }
    return sum;
  }, 0);

  const revenueChange = previousRevenue > 0
    ? ((stats.totalRevenue - previousRevenue) / previousRevenue) * 100
    : 0;

  const ordersChange = previousOrders.length > 0
    ? ((stats.totalOrders - previousOrders.length) / previousOrders.length) * 100
    : 0;

  // Top selling products
  const productSales = {};
  filteredOrders.forEach(order => {
    if (order.status !== 'cancelled') {
      order.items?.forEach(item => {
        if (!productSales[item.productId]) {
          productSales[item.productId] = {
            productId: item.productId,
            productName: item.productName,
            quantity: 0,
            revenue: 0,
            image: item.image
          };
        }
        productSales[item.productId].quantity += item.quantity;
        productSales[item.productId].revenue += item.price * item.quantity;
      });
    }
  });

  const topProducts = Object.values(productSales)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  // Recent orders
  const recentOrders = [...filteredOrders]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const StatCard = ({ title, value, icon: Icon, change, format = 'number', color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      red: 'bg-red-500',
      purple: 'bg-purple-500'
    };

    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <h3 className="text-2xl font-bold mt-2">
                {format === 'currency' ? `PKR ${value.toLocaleString()}` : value.toLocaleString()}
              </h3>
              {change !== undefined && change !== 0 && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {change > 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  <span>{Math.abs(change).toFixed(1)}% vs last period</span>
                </div>
              )}
            </div>
            <div className={`${colorClasses[color]} p-3 rounded-lg`}>
              <Icon className="text-white" size={24} />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4a1a1f] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Date Range Selector */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 size={24} />
              Dashboard Overview
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Calendar size={20} className="text-gray-600" />
              <select
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#4a1a1f]"
                defaultValue="thisMonth"
              >
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="last3Months">Last 3 Months</option>
                <option value="last6Months">Last 6 Months</option>
                <option value="allTime">All Time</option>
              </select>
              <span className="text-sm text-gray-600">
                {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
              </span>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={stats.totalRevenue}
          icon={DollarSign}
          format="currency"
          color="green"
          change={revenueChange}
        />
        <StatCard
          title="Total Orders"
          value={stats.totalOrders}
          icon={ShoppingCart}
          color="blue"
          change={ordersChange}
        />
        <StatCard
          title="Delivered Orders"
          value={stats.deliveredOrders}
          icon={Package}
          color="purple"
        />
        <StatCard
          title="Pending Orders"
          value={stats.pendingOrders}
          icon={AlertTriangle}
          color="yellow"
        />
      </div>

      {/* Inventory Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Inventory Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Products</p>
              <p className="text-2xl font-bold text-blue-900">{stats.totalProducts}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Low Stock</p>
              <p className="text-2xl font-bold text-yellow-900">{stats.lowStockProducts}</p>
              <p className="text-xs text-gray-500">{'<'} 5 items</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Out of Stock</p>
              <p className="text-2xl font-bold text-red-900">{stats.outOfStock}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Inventory Value</p>
              <p className="text-2xl font-bold text-green-900">
                PKR {stats.totalInventoryValue.toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Selling Products */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={20} />
              Top Selling Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No sales data available</p>
            ) : (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div key={product.productId} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#4a1a1f] text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="w-12 h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                      {product.image && (
                        <img
                          src={product.image}
                          alt={product.productName}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{product.productName}</p>
                      <p className="text-sm text-gray-600">{product.quantity} sold</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">PKR {product.revenue.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart size={20} />
              Recent Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No orders yet</p>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div key={order.orderId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{order.orderId}</p>
                      <p className="text-sm text-gray-600">
                        {order.customer?.firstName} {order.customer?.lastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(order.createdAt), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">PKR {order.totalAmount?.toLocaleString()}</p>
                      <span
                        className={`inline-block text-xs px-2 py-1 rounded-full ${
                          order.status === 'delivered'
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : order.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {stats.lowStockProducts > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-900">
              <AlertTriangle size={20} />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {products
                .filter(p => (p.stock || 0) < 5 && (p.stock || 0) > 0)
                .slice(0, 5)
                .map(product => (
                  <div key={product.id} className="flex items-center justify-between p-2 bg-white rounded">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded overflow-hidden">
                        {product.images?.[0] && (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <span className="font-medium">{product.name}</span>
                    </div>
                    <span className="text-red-600 font-bold">{product.stock} left</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
